import React, { useEffect, useRef, useState } from 'react';

/**
 * The tagline moment.
 *
 * One statement, given its own stretch of page. Words light up one at a time in
 * reading order as the section crosses the viewport, so the sentence is read at
 * the pace it was written rather than arriving all at once.
 *
 * Driven by IntersectionObserver, never a scroll listener: a listener fires on
 * every frame of every scroll and forces a reflow each time, which is what
 * makes pages like this feel heavy on a phone.
 *
 * Honours prefers-reduced-motion by showing the finished state immediately.
 */
export default function TaglineReveal({
  text = 'We do not sell signals and we do not sell courses. We run the systems, we post the numbers, and you can watch every trade as it happens.',
  className = '',
}) {
  const words = text.split(' ');
  const containerRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setActive(words.length); return; }

    // Each word is its own observation. The alternative — one observer plus
    // arithmetic on scroll position — is the thing that reintroduces reflow.
    const nodes = el.querySelectorAll('[data-word]');
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          seen.add(Number(entry.target.dataset.word));
          // Activate in reading order, so a fast scroll cannot light word 20
          // before word 3 and leave a hole in the sentence.
          let next = 0;
          while (seen.has(next)) next += 1;
          setActive((current) => Math.max(current, next));
        });
      },
      // Trigger line sits above the fold's midpoint, so words activate as they
      // rise into the comfortable reading band rather than at the very bottom.
      { rootMargin: '-45% 0px -35% 0px', threshold: 0 },
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [words.length]);

  return (
    <section className={`relative z-10 px-4 sm:px-6 lg:px-8 py-24 sm:py-32 ${className}`}>
      <div ref={containerRef} className="max-w-[680px] mx-auto">
        <p className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug">
          {words.map((word, i) => (
            <span
              key={`${word}-${i}`}
              data-word={i}
              className="transition-colors duration-700 ease-[cubic-bezier(0.32,0.72,0,1)]"
              style={{ color: i < active ? '#FFFFFF' : 'rgba(255,255,255,0.3)' }}
            >
              {word}
              {i < words.length - 1 ? ' ' : ''}
            </span>
          ))}
        </p>
      </div>
    </section>
  );
}
